"use client";

import {
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import type { ValidationRules, ValidationValue } from "../props";
import { useForm } from "./useForm";
import { useUuid } from "./useUuid";
import type { FieldMetadata, ValidationResult } from "../contexts/FormContext";
import { FormSectionContext } from "../contexts/FormSectionContext";
import { FormGroupContext } from "../contexts/FormGroupContext";
import { validateInput } from "../utils/validateInput";
import { useMaybePubSub } from "./usePubSub";
import { useDebug } from "./useDebug";

export type FormValidationConfig = {
  id?: string;
  validation?: ValidationRules;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  ref?: RefObject<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  >;
  /**
   * Optional metadata about the field for AI agent awareness.
   * Should be stable (memoized by callers) — not added to the effect dependency array.
   */
  metadata?: FieldMetadata;
  /**
   * The backend field key for this input (e.g. "applicant.income").
   * Passed by FormRow via cloneElement from the data-field-key attribute.
   * Merged into field metadata for AI agent awareness.
   * Should be stable — not added to the effect dependency array.
   */
  fieldKey?: string;
};

/**
 * useFormValidation is a hook that provides validation for a form input.
 */
export const useFormValidation = <T = ValidationValue>(
  value: T,
  config: FormValidationConfig,
) => {
  const generatedID = useUuid();
  const id = config.id ?? generatedID;
  const sectionKey = useContext(FormSectionContext);
  const groupInfo = useContext(FormGroupContext);
  const form = useForm();
  const formRef = useRef(form);
  formRef.current = form;
  const pubSub = useMaybePubSub();
  const { log } = useDebug();

  const [hasError, setHasError] = useState<boolean>(false);

  const stateValue = useRef<T>(value);
  useEffect(() => {
    stateValue.current = value;
    setHasError(false);
    formRef.current?.resetValidatable(id);
    log(
      "lightBlue",
      "useFormValidation",
      `value changed: ${config.name ?? id}`,
      value,
    );
    formRef.current?.notifyValueChanged();
  }, [value]);

  const disabled = !!(config.disabled || form?.disabled);

  const error = config.error ? true : hasError;

  const validate = useCallback((): ValidationResult => {
    if (!config.validation || config.validation.length === 0) {
      return {
        success: true,
        errors: [],
        value: stateValue.current,
        name: config.name,
      };
    }

    const { success, errors } = validateInput(
      stateValue.current,
      config.validation,
      config.name,
    );

    log(
      "orange",
      ["useFormValidation", "validate"],
      config.name,
      config.validation,
      stateValue.current,
      success ? "✅" : "❌",
    );

    if (success) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    return {
      success,
      errors: errors ?? [],
      value: stateValue.current,
      name: config.name,
    };
  }, [config.validation, config.name]);

  const validateRef = useRef(validate);
  useEffect(() => {
    validateRef.current = validate;
  }, [validate]);

  const getValueRef = useRef(() => stateValue.current as unknown);
  getValueRef.current = () => stateValue.current as unknown;

  useEffect(() => {
    const currentForm = formRef.current;
    if (!currentForm || !id) {
      return;
    }

    const stableValidate = () => validateRef.current();
    const stableGetValue = () => getValueRef.current();

    // Merge fieldKey, section/step, and group into metadata for AI agent awareness
    const enrichedMetadata: FieldMetadata | undefined =
      config.metadata || config.fieldKey || sectionKey || groupInfo
        ? {
            ...config.metadata,
            ...(config.fieldKey ? { fieldKey: config.fieldKey } : {}),
            ...(sectionKey ? { step: sectionKey } : {}),
            ...(groupInfo
              ? {
                  group: groupInfo.key,
                  groupName: groupInfo.name,
                  ...(groupInfo.parentKey
                    ? { parentGroup: groupInfo.parentKey }
                    : {}),
                }
              : {}),
          }
        : undefined;

    currentForm.registerValidatable(
      id,
      stableValidate,
      config.name,
      stableGetValue,
      enrichedMetadata,
    );

    return () => {
      currentForm.deregisterValidatable(id);
    };
    // formRef is used instead of form to prevent re-registration on every render
    // config.fieldKey, sectionKey, and groupInfo are stable — not added to dep array intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, config.name]);

  useEffect(() => {
    if (!pubSub || !id) {
      return;
    }

    const unsubscribe = pubSub.subscribe("inputFocus", (action) => {
      // console.log("[useFormValidation] Focusing", id, config.ref);

      if (action.id === id && config.ref) {
        config.ref.current?.focus();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [id, pubSub, config.ref]);

  return {
    id,
    error,
    disabled,
  };
};
